import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { ToastContext } from '~/components/toast-context'

/**
 * A toast says how something went — over whatever is on screen when it lands — and goes on
 * its own. It is for the outcome of work that left the surface it started on, like a send
 * that closed its dialog; an error a form can answer for itself stays in the form.
 */

interface Toast {
  readonly id: number
  readonly message: string
  readonly leaving: boolean
}

/** Long enough to read a sentence without hurrying, short enough to never feel stuck. */
const TOAST_HELD_MS = 5000
/** Matches the toast-out animation, so the row leaves the tree as the leave finishes. */
const TOAST_LEAVE_MS = 150

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([])
  const nextId = useRef(0)
  const timers = useRef(new Set<number>())

  useEffect(() => {
    const held = timers.current
    return () => {
      for (const timer of held) {
        window.clearTimeout(timer)
      }
    }
  }, [])

  const show = useCallback((message: string) => {
    const id = nextId.current++
    setToasts((held) => [...held, { id, message, leaving: false }])
    const away = window.setTimeout(() => {
      timers.current.delete(away)
      setToasts((held) =>
        held.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
      )
      const gone = window.setTimeout(() => {
        timers.current.delete(gone)
        setToasts((held) => held.filter((toast) => toast.id !== id))
      }, TOAST_LEAVE_MS)
      timers.current.add(gone)
    }, TOAST_HELD_MS)
    timers.current.add(away)
  }, [])

  return (
    <ToastContext.Provider value={show}>
      {children}
      {createPortal(
        // Above every dialog: a toast reports on work that may have closed the surface it
        // started from, so nothing may stand in front of it.
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex flex-col items-center gap-2 px-4">
          {toasts.map((toast) => (
            <p
              key={toast.id}
              role="status"
              className={`max-w-md rounded-full bg-surface-overlay px-5 py-3 text-center text-sm shadow-2xl ring-1 ring-white/10 ${
                toast.leaving ? 'toast-out' : 'toast-in'
              }`}
            >
              {toast.message}
            </p>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
